import React, { useCallback, useState } from "react"

import Paragraph from "decentraland-gatsby/dist/components/Text/Paragraph"
import SubTitle from "decentraland-gatsby/dist/components/Text/SubTitle"
import useAsyncEffect from "decentraland-gatsby/dist/hooks/useAsyncEffect"
import useFormatMessage from "decentraland-gatsby/dist/hooks/useFormatMessage"
import { Link } from "decentraland-gatsby/dist/plugins/intl"
import { navigate } from "decentraland-gatsby/dist/plugins/intl/utils"
import TokenList from "decentraland-gatsby/dist/utils/dom/TokenList"

import { useBlogContentfulQuery } from "../../../hooks/useBlogContentfulQuery"
import searchBig from "../../../images/magnify-big-dark-mode.svg"
import { searchBlog } from "../../../modules/algolia"
import { searchFormatWithPost } from "../../../modules/formats"
import locations from "../../../modules/locations"
import { Close } from "../../Icon/Close"
import LoadingText from "../../Loading/LoadingText"

import "./Search.css"

export type SearchProps = React.InputHTMLAttributes<HTMLInputElement> & {
  onClose?: (event: React.MouseEvent<HTMLElement>) => void
}

export default React.memo(function Search(props: SearchProps) {
  const { onClose, ...rest } = props
  const l = useFormatMessage()
  const [searchValue, setSearchValue] = useState("")
  const [selectedValue, setSelectedValue] = useState<number>(-1)
  const [searchResult, setSearchResult] = useState<
    Omit<SearchMatches, "selected" | "onMouseEnter">[]
  >([])

  const handleClose = useCallback((e) => {
    e.preventDefault()
    setSearchResult([])
    setSearchValue("")
    onClose && onClose(e)
  }, [])

  const handleSearchNavigation = useCallback(
    (e) => {
      const maxLength = searchResult.length > 3 ? 4 : searchResult.length
      if (e.key === "ArrowUp") {
        if (selectedValue <= 0) {
          setSelectedValue(searchResult.length > 3 ? 4 : searchResult.length)
        } else {
          setSelectedValue((prev) => prev - 1)
        }
      } else if (e.key === "ArrowDown") {
        if (selectedValue >= maxLength) {
          setSelectedValue(0)
        } else {
          setSelectedValue((prev) => prev + 1)
        }
      } else if (e.key === "Enter") {
        if (selectedValue === 4 || selectedValue === -1) {
          navigate(locations.search(searchValue))
        } else {
          navigate(searchResult[selectedValue].url)
        }
      }
    },
    [selectedValue, searchValue]
  )

  const { getBlogPost } = useBlogContentfulQuery()

  useAsyncEffect(async () => {
    if (searchValue.trim().length > 2) {
      const algoliaResponse = await searchBlog(searchValue, {
        hitsPerPage: 5,
      })
      const responseFormated = searchFormatWithPost(
        algoliaResponse,
        getBlogPost
      )
      setSearchResult(responseFormated)
    } else {
      setSearchResult([])
    }
  }, [searchValue])

  return (
    <div
      className={TokenList.join([
        "search-container",
        props.className,
        searchValue.length > 2 && "open-result",
      ])}
    >
      {searchValue.length > 2 && (
        <div className="search-container__overlay" onClick={handleClose}></div>
      )}
      <div className="search-container__input-content">
        <input
          {...rest}
          placeholder={props.placeholder || "Search..."}
          className="search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleSearchNavigation}
        />
        <div className="search-container__close" onClick={handleClose}>
          <Close dark />
        </div>
      </div>

      <ul className="search-results">
        {searchResult.slice(0, 4).map((result, key) => (
          <SearchResult
            matches={{
              ...result,
              selected: key === selectedValue,
              onMouseEnter: () => setSelectedValue(key),
            }}
            key={key}
          />
        ))}
        {searchResult.length > 4 && (
          <li
            className={TokenList.join([
              "search-results__more-results",
              selectedValue === 4 && "selected",
            ])}
            key={4}
            onMouseEnter={() => setSelectedValue(4)}
          >
            <Link href={locations.search(searchValue)}>
              {l("component.search.see_more_results")}
            </Link>
          </li>
        )}
        {searchValue.length > 2 && searchResult.length === 0 && (
          <li className="no-results">
            <div className="image">
              <img src={searchBig} />
            </div>
            <strong>{l("component.search.no_matches")}</strong>
            <span>{l("component.search.no_matches_subtitle")}</span>
          </li>
        )}
      </ul>
    </div>
  )
})

export type SearchMatches = {
  url: string
  image: string
  title: React.ReactNode[]
  description: React.ReactNode[]
  selected?: boolean
  onMouseEnter?: React.MouseEventHandler
}

export type SearchResultProps = {
  matches?: SearchMatches
  loading?: boolean
}

export function SearchResult(props: SearchResultProps) {
  const { matches, loading } = props

  return (
    <li
      className={TokenList.join([
        "search-row",
        matches?.selected && "selected",
        loading && "loading",
      ])}
      onMouseEnter={matches?.onMouseEnter}
    >
      <Link href={matches?.url}>
        <div
          className="search-row__image"
          style={
            !loading && matches?.image
              ? {
                  backgroundImage: `url(${matches.image})`,
                }
              : {}
          }
        ></div>
        {loading && (
          <div className="search-row__text">
            <LoadingText type="span" size="large" />
            <LoadingText type="span" size="medium" lines={3} />
          </div>
        )}
        {!loading && (
          <div className="search-row__text">
            <SubTitle>{matches?.title}</SubTitle>
            <Paragraph>{matches?.description}</Paragraph>
          </div>
        )}
      </Link>
    </li>
  )
}
